import { useState, useEffect } from "react";

// ═══════════════════════════════════════════════════════════════
// SHIPSWIFT CPMG DEMO — Responsive Interactive Walkthrough
// Mobile-first design. Self-contained mock data. No backend.
// ═══════════════════════════════════════════════════════════════

const C = {
  navy: "#0C1B3A", navyMid: "#162D5A", navyLight: "#1E3F7A",
  saffron: "#FF9933", saffronDark: "#E88A2D", saffronPale: "#FFF3E0",
  green: "#138808", greenPale: "#E8F5E9",
  white: "#FFFFFF", cream: "#FAFAF5", sand: "#F5F0E8",
  border: "#E0DAD0", text: "#1A1A1A", text2: "#5A5A5A", text3: "#9A9A9A",
  red: "#C62828", blue: "#1565C0",
};

const useW = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => { const h = () => setW(window.innerWidth); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return { w, mob: w < 640, tab: w >= 640 && w < 1024, desk: w >= 1024 };
};

const ORDERS = [
  { id: "SS-3F8A91B2", awb: "EE987654321IN", status: "delivered", sender: "Gujarat Ghee Co.", receiver: "Priya Sharma", city: "Mumbai", pin: "400001", wt: 2500, svc: "Speed Post", amt: 14200, cod: 0, mode: "Prepaid", booked: "2026-03-10", dlvd: "2026-03-14" },
  { id: "SS-7C2D45E1", awb: "EE123456789IN", status: "in_transit", sender: "Kutch Handicrafts", receiver: "Rahul Mehta", city: "Delhi", pin: "110001", wt: 1800, svc: "Speed Post", amt: 12800, cod: 0, mode: "Prepaid", booked: "2026-03-15", dlvd: null },
  { id: "SS-A1F03C9D", awb: "EE456789012IN", status: "out_for_delivery", sender: "Saurashtra Spices", receiver: "Meena Kumari", city: "Pune", pin: "411001", wt: 3200, svc: "Biz Parcel", amt: 9500, cod: 125000, mode: "COD", booked: "2026-03-14", dlvd: null },
  { id: "SS-B4E27F6A", awb: "EE789012345IN", status: "confirmed", sender: "Amul Outlet", receiver: "Suresh Patel", city: "Surat", pin: "395001", wt: 5000, svc: "Biz Parcel", amt: 8200, cod: 0, mode: "Prepaid", booked: "2026-03-16", dlvd: null },
  { id: "SS-D9C38E4B", awb: "EE345678901IN", status: "delivered", sender: "Madhubani Arts", receiver: "Anita Roy", city: "Kolkata", pin: "700001", wt: 800, svc: "Speed Post", amt: 6500, cod: 0, mode: "Prepaid", booked: "2026-03-08", dlvd: "2026-03-12" },
];
const TRACK = [
  { t: "10 Mar 09:12", e: "Booked — Waybill generated", l: "ShipSwift", i: "📋" },
  { t: "10 Mar 14:30", e: "Accepted at booking office", l: "Ahmedabad GPO", i: "📮" },
  { t: "10 Mar 22:15", e: "Dispatched from origin hub", l: "Ahmedabad NSH", i: "🚛" },
  { t: "11 Mar 06:45", e: "Received at transit hub", l: "Mumbai TMO", i: "📦" },
  { t: "12 Mar 08:20", e: "Dispatched to destination", l: "Mumbai RMS", i: "🚛" },
  { t: "13 Mar 10:30", e: "Received at delivery office", l: "Fort DO", i: "🏤" },
  { t: "13 Mar 11:45", e: "Out for delivery", l: "Beat 3, Fort", i: "🚶" },
  { t: "14 Mar 09:15", e: "Delivered — Signed by Priya S.", l: "400001", i: "✅" },
];
const ps = v => `₹${(v / 100).toLocaleString("en-IN")}`;

const Fade = ({ children, delay = 0, style = {} }) => {
  const [s, setS] = useState(false);
  useEffect(() => { const t = setTimeout(() => setS(true), delay); return () => clearTimeout(t); }, []);
  return <div style={{ opacity: s ? 1 : 0, transform: s ? "translateY(0)" : "translateY(8px)", transition: "all 0.4s ease-out", ...style }}>{children}</div>;
};

const G = ({ cols, gap = 12, children }) => {
  const { mob, tab } = useW();
  const c = mob ? (cols.m || 1) : tab ? (cols.t || 2) : (cols.d || 3);
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${c}, 1fr)`, gap }}>{children}</div>;
};

const SC = ({ icon, label, value, sub, color = C.navy, delay = 0 }) => (
  <Fade delay={delay}>
    <div style={{ background: C.white, borderRadius: 12, padding: "14px 16px", border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: color + "12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>{icon}</div>
        <span style={{ fontSize: 11, color: C.text3 }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: "-0.3px" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.text3, marginTop: 1 }}>{sub}</div>}
    </div>
  </Fade>
);

const Bg = ({ children, color = C.blue }) => (
  <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 14, fontSize: 10, fontWeight: 700, background: color + "18", color }}>{children}</span>
);

const SB = ({ status }) => {
  const m = { delivered: [C.green, "Delivered"], in_transit: [C.blue, "In Transit"], out_for_delivery: [C.saffron, "Out for Delivery"], confirmed: ["#6A1B9A", "Booked"] };
  const [c, l] = m[status] || [C.text3, status];
  return <Bg color={c}>{l}</Bg>;
};

const Sec = ({ icon, title, sub }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}><span style={{ fontSize: 18 }}>{icon}</span><h2 style={{ fontSize: 17, fontWeight: 800, color: C.navy, margin: 0 }}>{title}</h2></div>
    {sub && <p style={{ fontSize: 12, color: C.text2, margin: "3px 0 0 25px" }}>{sub}</p>}
  </div>
);

// ═══════ OVERVIEW ═══════
const Overview = () => {
  const { mob } = useW();
  return (<div>
    <Fade><div style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid}, ${C.navyLight})`, borderRadius: mob ? 12 : 18, padding: mob ? "24px 18px" : "40px 32px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -40, right: -20, width: 160, height: 160, borderRadius: "50%", background: C.saffron + "10" }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: mob ? 28 : 36 }}>📮</span>
          <div><h1 style={{ fontSize: mob ? 24 : 32, fontWeight: 800, color: C.white, margin: 0 }}>ShipSwift</h1><div style={{ fontSize: mob ? 9 : 11, color: C.saffron, fontWeight: 700, letterSpacing: 2 }}>INDIA POST SHIPPING PLATFORM</div></div>
        </div>
        <p style={{ fontSize: mob ? 12 : 14, color: "#FFFFFFCC", lineHeight: 1.7, maxWidth: 560, margin: "0 0 20px" }}>Technology platform enabling GCCI members and institutional sellers to ship via India Post with one-click booking, real-time tracking, automated COD settlement, and full GST compliance.</p>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: mob ? 14 : 20 }}>
          {[["147", "Active Sellers"], ["2,847", "Shipments"], ["₹38.4L", "Revenue"], ["84%", "Delivery Rate"]].map(([v, l], i) => (
            <div key={i} style={{ textAlign: "center" }}><div style={{ fontSize: mob ? 20 : 24, fontWeight: 800, color: C.saffron }}>{v}</div><div style={{ fontSize: 9, color: "#FFFFFF88", letterSpacing: "0.5px" }}>{l}</div></div>
          ))}
        </div>
      </div>
    </div></Fade>
    <Sec icon="⚡" title="How It Works" sub="Seller to doorstep in three steps" />
    <G cols={{ m: 1, t: 3, d: 3 }} gap={10}>
      {[["1", "📱", "Seller Books", "Enter recipient, select service, pay from wallet. Waybill generated via CEPT API.", C.saffron],
        ["2", "🚛", "India Post Delivers", "Pickup or drop-off. Real-time tracking flows to dashboard + buyer SMS.", C.green],
        ["3", "💰", "Auto Settlement", "Shipping from wallet. COD settled T+3. GST invoices auto-generated.", C.blue],
      ].map(([n, ic, t, d, color], i) => (
        <Fade key={i} delay={i * 80}><div style={{ background: C.white, borderRadius: 10, padding: 16, border: `1px solid ${C.border}`, borderLeft: `4px solid ${color}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}><div style={{ width: 26, height: 26, borderRadius: "50%", background: color + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color }}>{n}</div><span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{t}</span></div>
          <p style={{ fontSize: 11, color: C.text2, lineHeight: 1.5, margin: 0 }}>{d}</p>
        </div></Fade>))}
    </G>
    <div style={{ marginTop: 24 }}><Sec icon="🏛️" title="Why India Post via ShipSwift" />
      <G cols={{ m: 2, t: 3, d: 3 }} gap={8}>
        {[["🗺️", "1.56L Post Offices", "Deepest last-mile network"],["💸", "20-30% Lower", "NAF bulk discount"],["🏛️", "Govt Trust", "Trusted in Tier 2/3"],["📊", "Compliance", "Auto GST, TDS, recon"],["🔒", "Sovereign", "NIC/MeghRaj ready"],["🤝", "Channels", "GCCI, KVIC, TRIFED"]
        ].map(([ic, t, d], i) => (
          <Fade key={i} delay={i * 50}><div style={{ background: C.white, borderRadius: 8, padding: "12px 14px", border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{ic}</div><div style={{ fontSize: 11, fontWeight: 700, color: C.navy, marginBottom: 2 }}>{t}</div><div style={{ fontSize: 10, color: C.text2, lineHeight: 1.4 }}>{d}</div>
          </div></Fade>))}
      </G>
    </div>
  </div>);
};

// ═══════ DASHBOARD ═══════
const Dashboard = () => {
  const { mob } = useW();
  return (<div>
    <Fade><div style={{ display: "flex", flexDirection: mob ? "column" : "row", justifyContent: "space-between", alignItems: mob ? "flex-start" : "center", marginBottom: 18, gap: 8 }}>
      <div><h2 style={{ fontSize: mob ? 16 : 19, fontWeight: 800, color: C.navy, margin: 0 }}>Gujarat Ghee Co.</h2><p style={{ fontSize: 11, color: C.text3, margin: "2px 0 0" }}>GCCI · Ahmedabad · Active since Mar 2026</p></div>
      <div style={{ background: C.greenPale, padding: "5px 12px", borderRadius: 7 }}><span style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>Wallet: ₹48,500</span></div>
    </div></Fade>
    <G cols={{ m: 2, t: 4, d: 4 }} gap={10}>
      <SC icon="📦" label="Orders" value="42" sub="This month" color={C.navy} delay={0} />
      <SC icon="✅" label="Delivered" value="35" sub="83% rate" color={C.green} delay={50} />
      <SC icon="🚛" label="In Transit" value="5" color={C.blue} delay={100} />
      <SC icon="💰" label="Revenue" value="₹58.2K" color={C.saffron} delay={150} />
    </G>
    <div style={{ marginTop: 20 }}><Sec icon="📋" title="Recent Shipments" />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {ORDERS.map((o, i) => (
          <Fade key={o.id} delay={i * 40}><div style={{ background: C.white, borderRadius: 10, padding: mob ? "10px 12px" : "12px 16px", border: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: C.navy }}>{o.id}</span><SB status={o.status} />
              {o.mode === "COD" && <Bg color={C.saffron}>COD {ps(o.cod)}</Bg>}
            </div>
            <div style={{ fontSize: 11, color: C.text2 }}>{o.receiver} · {o.city} · {o.pin}</div>
            <div style={{ fontSize: 10, color: C.text3, marginTop: 1, display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
              <span>AWB: {o.awb} · {o.svc} · {o.wt}g</span><span>{o.dlvd ? `✓ ${o.dlvd}` : `Booked ${o.booked}`}</span>
            </div>
          </div></Fade>))}
      </div>
    </div>
  </div>);
};

// ═══════ BOOKING ═══════
const Booking = () => {
  const { mob } = useW();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);

  if (done) return (<Fade><div style={{ textAlign: "center", padding: mob ? "28px 10px" : "44px 20px" }}>
    <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.greenPale, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 14px" }}>✅</div>
    <h2 style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>Shipment Booked!</h2>
    <p style={{ color: C.text2, fontSize: 13 }}>AWB: <span style={{ fontFamily: "monospace", fontWeight: 700 }}>EE112233445IN</span></p>
    <p style={{ color: C.text3, fontSize: 11 }}>₹142 deducted from wallet · Label ready</p>
    <div style={{ margin: "16px auto", padding: 14, background: C.sand, borderRadius: 8, display: "inline-block", textAlign: "left" }}>
      <div style={{ background: C.white, border: `2px solid ${C.navy}`, borderRadius: 6, padding: 12, width: mob ? 220 : 240 }}>
        <div style={{ textAlign: "center", borderBottom: `1px solid ${C.border}`, paddingBottom: 5, marginBottom: 5 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.navy }}>INDIA POST — SPEED POST</div>
          <div style={{ fontFamily: "monospace", fontSize: 12, letterSpacing: 2, marginTop: 2 }}>EE112233445IN</div>
        </div>
        <div style={{ fontSize: 10, lineHeight: 1.6 }}><div><b>FROM:</b> Gujarat Ghee Co., 380001</div><div><b>TO:</b> Priya Sharma, Mumbai 400001</div><div><b>WT:</b> 2.5kg · <b>PREPAID</b> · ₹142</div></div>
        <div style={{ textAlign: "center", marginTop: 5, padding: 3, background: "#f5f5f5", borderRadius: 3, fontFamily: "monospace", fontSize: 8, letterSpacing: 3 }}>||||| |||| ||| |||| |||||</div>
      </div>
    </div>
    <div><button onClick={() => { setDone(false); setStep(1); }} style={{ background: C.saffron, color: C.white, border: "none", padding: "9px 20px", borderRadius: 7, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Book Another</button></div>
  </div></Fade>);

  return (<div>
    <Sec icon="📦" title="Book a Shipment" sub="Details → Rate → Confirm" />
    <Fade><div style={{ display: "flex", gap: 0, marginBottom: 20 }}>
      {["Details", "Rate", "Confirm"].map((s, i) => (
        <div key={i} style={{ flex: 1, textAlign: "center" }}>
          <div style={{ width: 24, height: 24, borderRadius: "50%", margin: "0 auto 3px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, background: step > i ? C.green : step === i + 1 ? C.saffron : C.border, color: step > i || step === i + 1 ? C.white : C.text3 }}>{i + 1}</div>
          <div style={{ fontSize: 9, color: step === i + 1 ? C.navy : C.text3, fontWeight: step === i + 1 ? 700 : 400 }}>{s}</div>
        </div>))}
    </div></Fade>
    <Fade><div style={{ background: C.white, borderRadius: 10, padding: mob ? 16 : 22, border: `1px solid ${C.border}`, maxWidth: 460 }}>
      {step === 1 && <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 12 }}>Recipient Details</h3>
        {[["Name", "Priya Sharma"],["Phone", "+91 98765 43210"],["Address", "42 Marine Drive, Fort"],["City", "Mumbai"],["PIN", "400001"],["Weight (g)", "2500"]].map(([l, v]) => (
          <div key={l} style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 10, color: C.text2, display: "block", marginBottom: 2 }}>{l}</label>
            <input defaultValue={v} readOnly style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, boxSizing: "border-box", background: C.cream }} />
          </div>))}
        <button onClick={() => setStep(2)} style={{ background: C.navy, color: C.white, border: "none", padding: "10px 22px", borderRadius: 7, fontWeight: 700, cursor: "pointer", marginTop: 4, fontSize: 12 }}>Get Rates →</button>
      </div>}
      {step === 2 && <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 3 }}>Available Services</h3>
        <p style={{ fontSize: 10, color: C.text3, marginBottom: 12 }}>380001 → 400001 · 2.5kg · Zone: ROI</p>
        {[{ n: "Speed Post", r: 14200, eta: "3-4 days", tag: "Recommended", tc: C.green },{ n: "Business Parcel", r: 9500, eta: "5-7 days", tag: "Cheapest", tc: C.blue }].map(s => (
          <div key={s.n} onClick={() => setStep(3)} style={{ background: s.tag === "Recommended" ? C.greenPale : C.white, border: `2px solid ${s.tag === "Recommended" ? C.green : C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 7, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{s.n}</span><Bg color={s.tc}>{s.tag}</Bg></div><div style={{ fontSize: 10, color: C.text3, marginTop: 2 }}>Est. {s.eta}</div></div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{ps(s.r)}</div>
          </div>))}
        <button onClick={() => setStep(1)} style={{ background: "transparent", color: C.text3, border: `1px solid ${C.border}`, padding: "8px 18px", borderRadius: 6, cursor: "pointer", marginTop: 4, fontSize: 11 }}>← Back</button>
      </div>}
      {step === 3 && <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 12 }}>Confirm & Pay</h3>
        <div style={{ background: C.sand, borderRadius: 7, padding: 12, marginBottom: 12 }}>
          {[["Service", "Speed Post"],["Route", "Ahmedabad → Mumbai"],["Weight", "2.5 kg"],["Shipping", "₹120.34"],["GST (18%)", "₹21.66"],["Total", "₹142.00"]].map(([l, v], i, a) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none", fontWeight: i === a.length - 1 ? 800 : 400, fontSize: i === a.length - 1 ? 14 : 11, color: i === a.length - 1 ? C.navy : C.text2 }}><span>{l}</span><span>{v}</span></div>))}
        </div>
        <div style={{ fontSize: 10, color: C.text3, marginBottom: 12 }}>Wallet: ₹48,500 → ₹48,358</div>
        <div style={{ display: "flex", gap: 7 }}>
          <button onClick={() => setDone(true)} style={{ background: C.green, color: C.white, border: "none", padding: "10px 20px", borderRadius: 7, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Confirm ✓</button>
          <button onClick={() => setStep(2)} style={{ background: "transparent", color: C.text3, border: `1px solid ${C.border}`, padding: "10px 16px", borderRadius: 6, cursor: "pointer", fontSize: 11 }}>← Back</button>
        </div>
      </div>}
    </div></Fade>
  </div>);
};

// ═══════ TRACKING ═══════
const TrackingPage = () => {
  const { mob } = useW();
  return (<div>
    <Sec icon="📍" title="Shipment Tracking" sub="Live via India Post CEPT" />
    <Fade><div style={{ background: C.white, borderRadius: 10, padding: mob ? 16 : 22, border: `1px solid ${C.border}`, maxWidth: 460 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 6 }}>
        <div><div style={{ fontSize: 9, color: C.text3, letterSpacing: 1 }}>AWB</div><div style={{ fontSize: mob ? 15 : 18, fontWeight: 800, color: C.navy, letterSpacing: 1, fontFamily: "monospace" }}>EE987654321IN</div></div>
        <div style={{ background: C.greenPale, padding: "4px 10px", borderRadius: 14 }}><span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>✓ Delivered</span></div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, marginBottom: 14, fontSize: 11, color: C.text2, flexWrap: "wrap", gap: 3 }}>
        <span><b>From:</b> Ahmedabad 380001</span><span><b>To:</b> Mumbai 400001</span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Tracking History</div>
      {TRACK.map((ev, i) => (
        <Fade key={i} delay={i * 50}><div style={{ display: "flex", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 16 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: i === TRACK.length - 1 ? C.green : C.border, flexShrink: 0 }} />
            {i < TRACK.length - 1 && <div style={{ width: 1.5, flex: 1, background: C.border, minHeight: 20 }} />}
          </div>
          <div style={{ paddingBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{ev.i} {ev.e}</div>
            <div style={{ fontSize: 9, color: C.text3, marginTop: 1 }}>{ev.l} — {ev.t}</div>
          </div>
        </div></Fade>))}
    </div></Fade>
    <Fade delay={250}><div style={{ marginTop: 14, background: C.saffronPale, borderRadius: 8, padding: "10px 14px", border: `1px solid ${C.saffron}30`, maxWidth: 460 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.saffronDark, marginBottom: 2 }}>📱 Buyer SMS at each milestone</div>
      <div style={{ fontSize: 10, color: C.text2 }}>Shipped → Out for delivery → Delivered. Footer: "Powered by GCCI × India Post"</div>
    </div></Fade>
  </div>);
};

// ═══════ GCCI CHANNEL ═══════
const ChannelPage = () => {
  const { mob } = useW();
  return (<div>
    <Fade><div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <div style={{ width: 38, height: 38, borderRadius: 8, background: C.greenPale, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏛️</div>
      <div><h2 style={{ fontSize: mob ? 15 : 18, fontWeight: 800, color: C.navy, margin: 0 }}>GCCI Channel Dashboard</h2><p style={{ fontSize: 10, color: C.text3, margin: 0 }}>Gujarat Chamber · Partner since Mar 2026</p></div>
    </div></Fade>
    <G cols={{ m: 2, t: 3, d: 3 }} gap={8}>
      <SC icon="👥" label="Sellers" value="147" sub="12 this week" color={C.navy} delay={0} />
      <SC icon="✅" label="Active" value="128" sub="87%" color={C.green} delay={40} />
      <SC icon="📦" label="Shipments" value="2,847" color={C.blue} delay={80} />
      <SC icon="💰" label="Revenue" value="₹38.4L" color={C.saffron} delay={120} />
      <SC icon="🎯" label="Delivery" value="84.0%" sub="Target 85%" color={C.green} delay={160} />
      <SC icon="↩️" label="RTO" value="5.0%" sub="<6% target" color={C.red} delay={200} />
    </G>
    <div style={{ marginTop: 20 }}><Sec icon="🏢" title="Top Sellers" sub="By volume" />
      <Fade delay={150}><div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: mob ? 360 : 0, fontSize: 11 }}>
          <thead><tr style={{ background: C.navy }}>
            {["Seller", "City", "Orders", "Revenue", "Del%"].map(h => <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: C.white, fontWeight: 600, fontSize: 10 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {[["Gujarat Ghee Co.", "Ahmedabad", "42", "₹58.2K", "95%"],["Kutch Handicrafts", "Bhuj", "38", "₹49.4K", "87%"],["Saurashtra Spices", "Rajkot", "35", "₹42.1K", "82%"],["Amul Outlet", "Anand", "31", "₹38.7K", "91%"],["Madhubani Arts", "Ahmedabad", "28", "₹31.5K", "89%"]].map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.cream, borderBottom: `1px solid ${C.border}` }}>
                {r.map((c, j) => <td key={j} style={{ padding: "7px 10px", color: j === 0 ? C.navy : C.text2, fontWeight: j === 0 ? 600 : 400 }}>{c}</td>)}
              </tr>))}
          </tbody>
        </table>
      </div></Fade>
    </div>
    <Fade delay={250}><div style={{ marginTop: 14, display: "flex", gap: 7, flexWrap: "wrap" }}>
      <button style={{ background: C.navy, color: C.white, border: "none", padding: "8px 16px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>📊 Export CSV</button>
      <button style={{ background: C.white, color: C.navy, border: `1px solid ${C.border}`, padding: "8px 16px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>📋 Export JSON</button>
    </div></Fade>
  </div>);
};

// ═══════ INTEGRATION ═══════
const IntegrationPage = () => {
  const { mob } = useW();
  return (<div>
    <Sec icon="🔗" title="India Post Integration" sub="CEPT v6.1 · NAF · Auto Reconciliation" />
    <G cols={{ m: 1, t: 2, d: 2 }} gap={10}>
      {[["CEPT v6.1 API", "XML/SFTP", "Waybill generation, booking manifest, tracking poll, tariff lookup. Fully automated.", "📡", C.navy],
        ["NAF Account", "Advance Deposit", "Prepaid deposit (no bank guarantee). Aarama holds NAF. Monthly billing.", "🏦", C.green],
        ["Rate Engine", "5-Zone Tariff", "Local/Metro/State/ROI/Special. Weight-slab tariff. Channel markup.", "💹", C.saffron],
        ["COD Settlement", "T+3 Cycle", "India Post collects → CEPT SFTP recon → Razorpay Payouts to merchant bank.", "💰", C.blue],
        ["Tracking", "30-min Poll", "Celery task polls India Post every 30 min. Events to webhook + buyer SMS.", "📍", "#6A1B9A"],
        ["Reconciliation", "Article-Level", "Monthly NAF bill matched per-article. Weight discrepancies auto-detected.", "🔍", C.red],
      ].map(([t, badge, d, ic, color], i) => (
        <Fade key={i} delay={i * 60}><div style={{ background: C.white, borderRadius: 10, padding: mob ? 14 : 18, border: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 7, background: color + "12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>{ic}</div>
            <div><div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{t}</div><Bg color={color}>{badge}</Bg></div>
          </div>
          <p style={{ fontSize: 11, color: C.text2, lineHeight: 1.55, margin: 0 }}>{d}</p>
        </div></Fade>))}
    </G>
    <Fade delay={300}><div style={{ marginTop: 18, background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`, borderRadius: 10, padding: mob ? "14px 16px" : "18px 22px", color: C.white }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>🔒 Sovereign Deployment Ready</div>
      <p style={{ fontSize: 11, lineHeight: 1.6, color: "#FFFFFFCC", margin: 0 }}>Deployable on NIC/MeghRaj cloud. AI components support air-gapped local inference. Zero data leaves Indian govt enclave. GIGW 3.0 compliant.</p>
    </div></Fade>
  </div>);
};

// ═══════ MAIN ═══════
const TABS = [
  { k: "overview", l: "Overview", i: "🏠" }, { k: "dashboard", l: "Merchant", i: "📊" },
  { k: "booking", l: "Book", i: "📦" }, { k: "tracking", l: "Track", i: "📍" },
  { k: "channel", l: "GCCI", i: "🏛️" }, { k: "integration", l: "India Post", i: "🔗" },
];

export default function App() {
  const [tab, setTab] = useState("overview");
  const { mob } = useW();
  const Scr = { overview: Overview, dashboard: Dashboard, booking: Booking, tracking: TrackingPage, channel: ChannelPage, integration: IntegrationPage }[tab];

  return (
    <div style={{ minHeight: "100vh", background: C.cream }}>
      <header style={{ background: C.navy, padding: "0 14px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: mob ? 44 : 50 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: mob ? 20 : 24 }}>📮</span>
            <span style={{ fontSize: mob ? 16 : 18, fontWeight: 800, color: C.white }}>ShipSwift</span>
            <span style={{ fontSize: 8, color: C.saffron, fontWeight: 700, letterSpacing: 2, marginLeft: 2, background: C.saffron + "22", padding: "1px 5px", borderRadius: 3 }}>DEMO</span>
          </div>
          {!mob && <div style={{ color: "#FFFFFF66", fontSize: 10 }}>Aarama Ventures LLP · CPMG Review</div>}
        </div>
      </header>
      <nav style={{ background: C.white, borderBottom: `1px solid ${C.border}`, overflowX: "auto", position: "sticky", top: mob ? 44 : 50, zIndex: 40, WebkitOverflowScrolling: "touch" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", padding: "0 6px", minWidth: "max-content" }}>
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} style={{
              padding: mob ? "8px 10px" : "10px 14px", border: "none", background: "transparent", cursor: "pointer",
              fontSize: mob ? 10 : 11, fontWeight: tab === t.k ? 700 : 500,
              color: tab === t.k ? C.red : C.text2,
              borderBottom: tab === t.k ? `2px solid ${C.red}` : "2px solid transparent",
              whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 3,
            }}><span style={{ fontSize: mob ? 13 : 14 }}>{t.i}</span>{t.l}</button>
          ))}
        </div>
      </nav>
      <main style={{ maxWidth: 960, margin: "0 auto", padding: mob ? "16px 12px 40px" : "24px 18px 48px" }}><Scr /></main>
      <footer style={{ background: C.navy, padding: "14px 14px", textAlign: "center" }}>
        <div style={{ color: "#FFFFFF55", fontSize: 10 }}>Aarama Ventures LLP · ShipSwift · Production-ready · March 2026</div>
        <div style={{ color: "#FFFFFF33", fontSize: 9, marginTop: 2 }}>Confidential — Department of Posts</div>
      </footer>
    </div>
  );
}
